// /admin/profesores/:id/descuentos — configurar descuentos del profesor
// (% por clase_tipo y % por bono_tipo). Los descuentos se aplican cuando
// el profesor reserva como alumno (B3) o compra bono (B4) — SS7.2.5.
//
// UI: tabs "Clases" / "Bonos". Por fila: input pct (0-100) + checkbox activo.
// Save manda TODOS los items al endpoint PUT (backend resuelve noop_igual /
// rotada / desactivada / creada según comparación con estado actual).

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Loader2, Check, Percent, Sparkles, Tag } from 'lucide-react';

import { getProfesorDescuentos, putProfesorDescuentos } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

function nombrePara(item, lang) {
  return item[`nombre_${lang}`] || item.nombre_es || '';
}

export default function AdminProfesorDescuentos() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const { id } = useParams();
  const navigate = useNavigate();

  const [profesor, setProfesor] = useState(null);
  const [clases, setClases] = useState([]);
  const [bonos, setBonos] = useState([]);
  const [originalForm, setOriginalForm] = useState(null); // snapshot para dirty
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState('clases');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getProfesorDescuentos(id)
      .then((d) => {
        if (cancelled) return;
        setProfesor(d.profesor);
        setClases(d.clases || []);
        setBonos(d.bonos || []);
        const f = {
          clases: Object.fromEntries((d.clases || []).map((c) => [
            c.clase_tipo_id,
            {
              pct: c.descuento ? String(c.descuento.descuento_pct) : '0',
              activo: !!c.descuento,
            },
          ])),
          bonos: Object.fromEntries((d.bonos || []).map((b) => [
            b.bono_tipo_id,
            {
              pct: b.descuento ? String(b.descuento.descuento_pct) : '0',
              activo: !!b.descuento,
            },
          ])),
        };
        setForm(f);
        setOriginalForm(JSON.parse(JSON.stringify(f)));
      })
      .catch((e) => { if (!cancelled) setLoadError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const dirty = useMemo(() => {
    if (!form || !originalForm) return false;
    return JSON.stringify(form) !== JSON.stringify(originalForm);
  }, [form, originalForm]);

  const setItem = (kind, refId, patch) => {
    setForm((f) => ({
      ...f,
      [kind]: { ...f[kind], [refId]: { ...f[kind][refId], ...patch } },
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setOkMsg(null);
    setErrMsg(null);
    // Validar pcts
    const errs = [];
    for (const arr of [['clases', clases, 'clase_tipo_id'], ['bonos', bonos, 'bono_tipo_id']]) {
      const [kind, items, _refKey] = arr;
      for (const it of items) {
        const refId = it[_refKey];
        const pctStr = form[kind][refId]?.pct;
        const pctNum = Number(pctStr);
        if (!Number.isFinite(pctNum) || pctNum < 0 || pctNum > 100) {
          errs.push(`${nombrePara(it, lang)}: ${t('admin.profesores.descuentos.err_pct')}`);
        }
      }
    }
    if (errs.length) {
      setErrMsg(errs.join(' · '));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        clases: clases.map((c) => ({
          clase_tipo_id: c.clase_tipo_id,
          descuento_pct: Number(form.clases[c.clase_tipo_id].pct),
          activo: !!form.clases[c.clase_tipo_id].activo,
        })),
        bonos: bonos.map((b) => ({
          bono_tipo_id: b.bono_tipo_id,
          descuento_pct: Number(form.bonos[b.bono_tipo_id].pct),
          activo: !!form.bonos[b.bono_tipo_id].activo,
        })),
      };
      const resp = await putProfesorDescuentos(id, payload);
      // Refrescar foto desde respuesta
      setClases(resp.clases || []);
      setBonos(resp.bonos || []);
      const nuevoForm = {
        clases: Object.fromEntries((resp.clases || []).map((c) => [
          c.clase_tipo_id,
          { pct: c.descuento ? String(c.descuento.descuento_pct) : '0', activo: !!c.descuento },
        ])),
        bonos: Object.fromEntries((resp.bonos || []).map((b) => [
          b.bono_tipo_id,
          { pct: b.descuento ? String(b.descuento.descuento_pct) : '0', activo: !!b.descuento },
        ])),
      };
      setForm(nuevoForm);
      setOriginalForm(JSON.parse(JSON.stringify(nuevoForm)));
      setOkMsg(t('admin.profesores.descuentos.guardado_ok'));
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      const i18nKey = code ? `admin.profesores.descuentos.errores.${code}` : null;
      const fallback = t('admin.profesores.descuentos.errores.generico');
      setErrMsg(i18nKey ? t(i18nKey, { defaultValue: fallback }) : fallback);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading"><Loader2 size={20} className="spin" /> <span>{t('admin.cargando')}</span></div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="admin-page">
        <div className="admin-error"><AlertCircle size={20} /><span>{loadError.message}</span></div>
      </div>
    );
  }

  const itemsActuales = tab === 'clases' ? clases : bonos;
  const refKey = tab === 'clases' ? 'clase_tipo_id' : 'bono_tipo_id';

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <button type="button" className="btn-link-back" onClick={() => navigate('/admin/profesores')}>
            <ArrowLeft size={16} /> {t('admin.volver')}
          </button>
          <h1 className="admin-page-titulo">
            <Percent size={22} strokeWidth={1.5} /> {t('admin.profesores.descuentos.titulo')}
          </h1>
          <p className="admin-page-sub">
            {t('admin.profesores.descuentos.sub', { nombre: profesor?.nombre_publico || '' })}
          </p>
        </div>
      </header>

      <div className="admin-tabs">
        <button
          type="button"
          className={'admin-tab' + (tab === 'clases' ? ' active' : '')}
          onClick={() => setTab('clases')}
        >
          <Sparkles size={16} strokeWidth={1.5} /> {t('admin.profesores.descuentos.tab_clases')}
          <span className="admin-tab-count">{clases.length}</span>
        </button>
        <button
          type="button"
          className={'admin-tab' + (tab === 'bonos' ? ' active' : '')}
          onClick={() => setTab('bonos')}
        >
          <Tag size={16} strokeWidth={1.5} /> {t('admin.profesores.descuentos.tab_bonos')}
          <span className="admin-tab-count">{bonos.length}</span>
        </button>
      </div>

      <form onSubmit={submit}>
        <div className="admin-card">
          {itemsActuales.length === 0 ? (
            <div className="admin-empty">{t('admin.profesores.descuentos.vacio')}</div>
          ) : (
            <table className="admin-tabla">
              <thead>
                <tr>
                  <th>{t('admin.profesores.descuentos.col_nombre')}</th>
                  <th className="admin-col-num">{t('admin.profesores.descuentos.col_pct')}</th>
                  <th>{t('admin.profesores.descuentos.col_activo')}</th>
                </tr>
              </thead>
              <tbody>
                {itemsActuales.map((it) => {
                  const refId = it[refKey];
                  const valor = form?.[tab]?.[refId] || { pct: '0', activo: false };
                  return (
                    <tr key={refId}>
                      <td>
                        <span>{nombrePara(it, lang)}</span>
                        {tab === 'bonos' && it.tipo && (
                          <span className="admin-cell-meta">{' · '}{it.tipo}</span>
                        )}
                      </td>
                      <td className="admin-col-num">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={valor.pct}
                          onChange={(e) => setItem(tab, refId, { pct: e.target.value })}
                          className="admin-input-num"
                          aria-label={t('admin.profesores.descuentos.col_pct')}
                        />
                        <span className="admin-cell-meta">{' %'}</span>
                      </td>
                      <td>
                        <label className="admin-switch">
                          <input
                            type="checkbox"
                            checked={!!valor.activo}
                            onChange={(e) => setItem(tab, refId, { activo: e.target.checked })}
                          />
                          <span>{valor.activo ? t('admin.profesores.descuentos.activo_si') : t('admin.profesores.descuentos.activo_no')}</span>
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {okMsg && (
          <div className="admin-msg-ok"><Check size={18} /> {okMsg}</div>
        )}
        {errMsg && (
          <div className="admin-error"><AlertCircle size={18} /><span>{errMsg}</span></div>
        )}

        <div className="admin-form-actions">
          <button type="submit" className="btn-primary" disabled={!dirty || saving}>
            {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
            {' '}{saving ? t('admin.profesores.descuentos.guardando') : t('admin.profesores.descuentos.btn_guardar')}
          </button>
        </div>
      </form>
    </div>
  );
}
